import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription, Observable } from 'rxjs';
import { startWith, map } from 'rxjs/operators';

import { OriginDetail } from 'src/app/shared/models/classes/origin/origin-detail';
import { OrganismService } from 'src/app/shared/services/organism.service';
import { CitizenService } from 'src/app/shared/services/citizen.service';
import { UiService } from 'src/app/shared/services/ui.service';
import { LanguageService } from 'src/app/shared/services/language.service';

@Component({
    selector: 'app-citizen-select-form',
    templateUrl: './citizen-select-form.component.html',
    styleUrls: ['./citizen-select-form.component.scss']
})
export class CitizenSelectFormComponent implements OnInit, OnDestroy {
    private loadingStateSub: Subscription;
    private langSub: Subscription;

    organismForm: FormGroup;

    options: OriginDetail[];
    filteredOptions: Observable<OriginDetail[]>;

    isLoading = true;

    constructor(
        private organismSrv: OrganismService,
        private citizenSrv: CitizenService,
        private uiSrv: UiService,
        private languageSrv: LanguageService
    ) { }

    ngOnInit() {
        this.getList();
        this.initSubscription();
    }

    onSubmit(): void {
        const formValue: string | OriginDetail = this.organismForm.value.organism;
        let selCode: string;

        if(typeof formValue === 'string') {
            const optFound = this.options.find(o => o.text.shortLabel.toLowerCase() === formValue.toLowerCase()
                || o.text.fullLabel.toLowerCase() === formValue.toLowerCase());
            selCode = optFound ? optFound.code : null;
        } else {
            selCode = formValue.code;
        }

        if(selCode) {
            this.isLoading = true;
            this.citizenSrv.get(selCode)
            .subscribe(() => {
                this.isLoading = false;
            });
        }
    }

    displayFn(origin?: OriginDetail): string | undefined {
        return origin ? origin.text.fullLabel : undefined;
    }

    private initSubscription(): void {
        this.loadingStateSub = this.uiSrv.loadingStateChanged
        .subscribe(loadingState => {
            this.isLoading = loadingState;
        });

        this.langSub = this.languageSrv.languageChanged
        .subscribe(() => {
            this.isLoading = true;
            this.getList();
        });
    }

    private getList(): void {
        this.organismSrv.getList()
        .subscribe(organismList => {
            this.options = organismList;
            this.initForm();
            this.isLoading = false;
        });
    }

    private initForm(): void {
        this.organismForm = new FormGroup({
            organism: new FormControl('', { validators: [Validators.required] })
        });

        this.setFilteredOptions();
    }

    private setFilteredOptions(): void {
        this.filteredOptions = this.organismForm.controls.organism.valueChanges
            .pipe(
                startWith<string | OriginDetail>(''),
                map(value => {
                    if (typeof value === 'string') {
                        return value;
                    } else {
                        return value.text.fullLabel;
                    }
                }),
                map(name => name ? this._filter(name) : this.options.slice())
            );
    }

    private _filter(value: string): OriginDetail[] {
        const filterValue = value.toLowerCase();

        return this.options.filter(option => option.text.fullLabel.toLowerCase().includes(filterValue));
    }

    ngOnDestroy() {
        if(this.loadingStateSub) {
            this.loadingStateSub.unsubscribe();
        }
        if(this.langSub) {
            this.langSub.unsubscribe();
        }
    }
}
